import { DeleteIcon } from '@chakra-ui/icons';
import { Box, Button, Card, CardBody, Flex, Grid, GridItem, Heading, Spacer, Table, TableContainer, Tbody, Td, Text, Tr } from '@chakra-ui/react';
import { useFormik } from 'formik';
import { parsePhoneNumber } from 'libphonenumber-js/min';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { urlNavigate } from '../../../helper/utils/navigation';
import { convertDateFormat } from '../../../helper/utils/utils.date';
import { doAddMinor, doCreateWaiverChild, doDeleteMinor, doUpdateWaiverChild } from '../../../redux/waiver/waiver.actions';
import AddMinor from '../../waiver/AddMinor';
import EditMinor from '../../waiver/EditMinor';

export default function AccountDetailsChildForm() {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { t } = useTranslation();
    const { waiver = {}, minor = [], loadingWaiver: isLoading = false } = useSelector((s) => s?.waiver);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            fullname: '',
            birthDate: '',
            email: '',
            phone: '',
            gender: '',
            country: { value: 'ID', label: 'Indonesia' },
            city: { value: '', label: '' },
            address: '',
            childrens: [],
        },
        onSubmit: (values) => {
            const minors = values.childrens.map((el) => ({ ...el, parentId: waiver?.phone ?? '' })).filter((el) => el?.isNew);

            const payload = {
                parentID: waiver['id'] || '',
                minor: minors?.map((minor) => {
                    return {
                        childrenID: minor['childrenID'] || '',
                        name: minor['name'] || '',
                        gender: (minor['gender'] || '') === 'male' ? '1' : '0',
                        birthdate: minor['birthDate'] || '',
                    };
                }),
            };

            dispatch(doCreateWaiverChild(payload, navigate));
        },
    });

    const handleMinor = (val) => {
        const a = [...minor, { ...val, isNew: true }].map((el, idx) => ({ ...el, key: idx }));
        dispatch(doAddMinor(a));
    };

    const handleUpdateMinor = (val) => {
        const payload = {
            parentID: val['parentCode'] || '',
            childrenID: val['childrenID'] || '',
            childrenName: val['name'] || '',
            gender: (val['gender'] || '') === 'male' ? '1' : '0',
            birthdate: val['birthDate'] || '',
        };

        dispatch(doUpdateWaiverChild(payload));
    };

    const handleDelete = (el) => {
        const filter = minor.filter((ch) => ch?.key !== el?.key);
        dispatch(doDeleteMinor(filter));
    };

    useEffect(() => {
        if (waiver) {
            formik.setValues({
                fullname: waiver?.fullname,
                birthDate: waiver?.birthDate,
                email: waiver?.email,
                phone: parsePhoneNumber(waiver?.phone ?? '', 'ID').formatInternational(),
                gender: +(waiver?.gender || '1') == 1 ? 'male' : 'female',
                country: { value: 'ID', label: 'Indonesia' },
                city: { value: '', label: '' },
                address: waiver?.address,
                childrens: minor,
            });
        }
    }, [waiver, minor]);

    return (
        <Box as='form' onSubmit={formik.handleSubmit}>
            <Box>
                <Heading size='lg' color='gray.800' mb={2}>
                    {t('any_under_18')}
                </Heading>
                <Text color='gray.600' fontSize='md'>
                    Manage your children
                </Text>
            </Box>
            <Box bg='white' borderRadius='2xl' p={5} py={10} shadow='sm' border='1px' borderColor='gray.100' mt={5}>
                {minor.length === 0 ? (
                    <Card variant='outline' bg='gray.50'>
                        <CardBody>
                            <Text textAlign='center' color='gray.500' fontSize='sm'>
                                No children added yet. Click the button below to add.
                            </Text>
                        </CardBody>
                    </Card>
                ) : (
                    <Grid w='full' gridTemplateColumns={{ base: 'repeat(1, 1fr)', lg: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }} gap={5}>
                        {minor.map((el) => (
                            <GridItem>
                                <Card key={el?.key} w='full' variant='outline' bg='primary.50' colorScheme='primary'>
                                    <CardBody>
                                        <TableContainer>
                                            <Table variant='unstyled' size='sm'>
                                                <Tbody>
                                                    <Tr>
                                                        <Td fontSize='sm' color='gray.600' maxW={{ base: '10px', md: 'full' }}>
                                                            {t('full_name')}
                                                        </Td>
                                                        <Td textAlign='right' fontWeight={600}>
                                                            {el?.name ?? ''}
                                                        </Td>
                                                    </Tr>
                                                    <Tr>
                                                        <Td fontSize='sm' color='gray.600' maxW={{ base: '10px', md: 'full' }}>
                                                            {t('birth_date')}
                                                        </Td>
                                                        <Td textAlign='right' fontWeight={600}>
                                                            {convertDateFormat(el?.birthDate ?? '')}
                                                        </Td>
                                                    </Tr>
                                                    {/* <Tr>
                                                        <Td fontSize='sm' color='gray.600' maxW={{ base: '10px', md: 'full' }}>
                                                            Gender
                                                        </Td>
                                                        <Td textAlign='right' fontWeight={600} textTransform='capitalize'>
                                                            {(el?.gender ?? '0') == '1' ? 'Male' : 'Female'}
                                                        </Td>
                                                    </Tr> */}
                                                    <Tr>
                                                        <Td fontSize='sm' color='gray.600' maxW={{ base: '10px', md: 'full' }}>
                                                            Status
                                                        </Td>
                                                        <Td textAlign='right' fontWeight={600} textTransform='uppercase'>
                                                            ACTIVE | {+(el?.statusCode || '0') == 1 ? 'MEMBER' : 'Reguler'}
                                                        </Td>
                                                    </Tr>
                                                </Tbody>
                                            </Table>
                                        </TableContainer>
                                        <Spacer />
                                        {el?.isNew && (
                                            <Flex w='full' justifyContent='end' mt={2}>
                                                <Button size='sm' colorScheme='red' variant='ghost' leftIcon={<DeleteIcon />} onClick={() => handleDelete(el)}>
                                                    {t('remove')}
                                                </Button>
                                            </Flex>
                                        )}
                                        {!el?.isNew && (
                                            <Flex w='full' justifyContent='end' mt={2}>
                                                <EditMinor handleMinor={handleUpdateMinor} minor={el} />
                                            </Flex>
                                        )}
                                    </CardBody>
                                </Card>
                            </GridItem>
                        ))}
                    </Grid>
                )}

                {/* Add Children Button */}
                <AddMinor handleMinor={handleMinor} />
            </Box>
            <Box mt={12}>
                <Flex w='full' alignItems='center' justifyContent='end' gap={3}>
                    <Button
                        type='button'
                        variant='ghost'
                        bg='gray.100'
                        color='red.800'
                        size='lg'
                        fontSize='md'
                        borderRadius='lg'
                        fontWeight='semibold'
                        _hover={{
                            bg: 'red.600',
                            color: 'white',
                            transform: 'translateY(-2px)',
                            shadow: 'lg',
                        }}
                        transition='all 0.2s'
                        mt={4}
                        onClick={() => navigate(urlNavigate('dashboard'))}>
                        Cancel
                    </Button>
                    <Button
                        type='submit'
                        bg='green.600'
                        color='white'
                        size='lg'
                        fontSize='md'
                        borderRadius='lg'
                        fontWeight='semibold'
                        isLoading={isLoading}
                        _hover={{
                            bg: 'green.900',
                            transform: 'translateY(-2px)',
                            shadow: 'lg',
                        }}
                        transition='all 0.2s'
                        mt={4}>
                        Save Changes
                    </Button>
                </Flex>
            </Box>
        </Box>
    );
}
